/**
 * App-shell service worker registration.
 *
 * Strictly guarded — never registers in dev, inside an iframe, or on any
 * Lovable preview hostname. Supports `?sw=off` as a kill switch that
 * unregisters the app-shell worker only (the push worker at
 * /push-sw.js is owned by src/lib/push and is left alone).
 */

const APP_SW_URL = "/sw.js";

function isRefusedContext(): boolean {
  if (typeof window === "undefined") return true;
  if (!import.meta.env.PROD) return true;
  if (window.self !== window.top) return true;

  const host = window.location.hostname;
  if (host.startsWith("id-preview--") || host.startsWith("preview--")) return true;
  if (host === "lovableproject.com" || host.endsWith(".lovableproject.com")) return true;
  if (host === "lovableproject-dev.com" || host.endsWith(".lovableproject-dev.com")) return true;
  if (host === "beta.lovable.dev" || host.endsWith(".beta.lovable.dev")) return true;

  const url = new URL(window.location.href);
  if (url.searchParams.get("sw") === "off") return true;

  return false;
}

async function unregisterAppShell() {
  if (!("serviceWorker" in navigator)) return;
  const regs = await navigator.serviceWorker.getRegistrations();
  for (const r of regs) {
    const url = r.active?.scriptURL || r.installing?.scriptURL || r.waiting?.scriptURL || "";
    if (url.endsWith(APP_SW_URL)) {
      try { await r.unregister(); } catch { /* ignore */ }
    }
  }
}

export async function registerAppShellSW() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;

  if (isRefusedContext()) {
    // Kill-switch path: clean up any previously registered app-shell worker.
    await unregisterAppShell().catch(() => {});
    return;
  }

  try {
    // Dynamic import so the workbox-window code only ships in production.
    const { Workbox } = await import("workbox-window");
    const wb = new Workbox(APP_SW_URL, { scope: "/" });
    wb.addEventListener("waiting", () => {
      // Auto-skip waiting so newest deploy takes effect on next nav.
      wb.messageSkipWaiting();
    });
    await wb.register();
  } catch (e) {
    console.warn("[appShellSW] registration failed", e);
  }
}
